// AddMembersDrawer — "Add members" (ADR-083 §2): the people of the project on the
// family's picker (the person lookup's `members` rule — every status; a person
// already on the team is greyed and says so), and whether the added ones can be
// assigned (on by default; off for a manager or a NOC person who follows the
// team's work without doing it). One drawer, one PUT-like POST; the server adds
// the active members and leaves the ones already there as they are.

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UserLookupRow } from '@ems-app/schemas/users/lookup';
import type { TeamDetail } from '@ems-app/schemas/teams/responses';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { usePersonFace } from '../../components/patterns/lookupFaces.js';
import { Picker } from '../../components/patterns/Picker.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { useAddTeamMembers } from '../../hooks/useTeamMutations.js';
import { personSource } from '../../hooks/useUsers.js';

const schema = z.object({ user_ids: z.array(z.string()).min(1).max(50), can_be_assigned: z.boolean() });
type Values = z.infer<typeof schema>;

export interface AddMembersDrawerProps {
  projectId: string;
  team: Pick<TeamDetail, 'id' | 'name' | 'members'>;
  open: boolean;
  onClose: () => void;
}

export function AddMembersDrawer({ projectId, team, open, onClose }: AddMembersDrawerProps) {
  const { t } = useTranslation('teams');
  const add = useAddTeamMembers(projectId);
  const personFace = usePersonFace();
  const onTeam = useMemo(() => new Set(team.members.map((m) => m.user_id)), [team.members]);
  // The project's people; a person already on the team is greyed with the reason on the second line.
  const source = useMemo(
    () =>
      personSource(projectId, 'members', (row: UserLookupRow) => {
        const base = personFace(row);
        return onTeam.has(row.id) ? { ...base, description: t('addMembers.alreadyOn'), disabled: true } : base;
      }),
    [projectId, personFace, onTeam, t],
  );

  const shell = useFormShell<Values>({
    schema,
    // A create (the members are new to the team — no stamp): a fresh draft on every open, the container's re-seed.
    row: null,
    values: () => ({ user_ids: [], can_be_assigned: true }),
    onSubmit: async (v) => {
      await add.mutateAsync({ teamId: team.id, body: { user_ids: v.user_ids, can_be_assigned: v.can_be_assigned } });
    },
    onSuccess: onClose,
  });
  const e = shell.form.formState.errors;

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('detail.addMembers')} description={team.name} submitLabel={t('addMembers.submit')}>
      <Controller
        control={shell.form.control}
        name="user_ids"
        render={({ field }) => (
          <Picker multiple selectedDisplay="tags" source={source} label={t('addMembers.people')} placeholder={t('addMembers.placeholder')} required value={field.value} onChange={(next) => field.onChange(next)} error={e.user_ids ? ' ' : undefined} />
        )}
      />
      <Controller
        control={shell.form.control}
        name="can_be_assigned"
        render={({ field }) => <Checkbox checked={field.value} onChange={(on) => field.onChange(on)} name={field.name} label={t('addMembers.canBeAssigned')} description={t('addMembers.canBeAssignedHelp')} />}
      />
    </DrawerForm>
  );
}
